import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../../services/auth.service";
import Loader from "../../components/Loader/Loader";
import { ProfileResponse, User } from "../../types";
import "./Dashboard.scss";

const Dashboard: React.FC<{ mode: "user" | "admin" }> = ({ mode }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [profileSaving, setProfileSaving] = useState<boolean>(false);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [profileError, setProfileError] = useState<string>("");
  const [profileSuccess, setProfileSuccess] = useState<string>("");
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [profileForm, setProfileForm] = useState({
    name: "",
    login: "",
    email: "",
    phone: "",
    image: "",
  });

  const fetchUserInfo = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response =
        mode === "user"
          ? await authService.validateUser()
          : await authService.validateAdmin();

      if (response.valid && response.user) {
        setUser(response.user);
        authService.saveUser(response.user);
      } else {
        throw new Error("Invalid session");
      }
    } catch (error) {
      authService.clearUser();
      navigate(`/login?mode=${mode}`);
    } finally {
      setLoading(false);
    }
  }, [navigate, mode]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  // Signed out on another SATashkent site → signed out here too: re-check when
  // the tab comes back into view and every two minutes while it is open.
  useEffect(() => {
    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const response =
          mode === "user" ? await authService.validateUser() : await authService.validateAdmin();
        if (response.valid) return;
      } catch {
        return; // a network blip is not a sign-out
      }
      authService.clearUser();
      navigate(`/login?mode=${mode}`);
    };
    const timer = window.setInterval(check, 2 * 60 * 1000);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [mode, navigate]);

  const handleLogout = async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      // Continue with logout
    } finally {
      authService.clearAllData();
      window.location.href = `/login?mode=${mode}&logout=true`;
    }
  };

  const syncCachedUser = (nextProfile: ProfileResponse) => {
    const cachedUser = authService.getUser();
    if (!cachedUser) return;

    const nextUser: User = {
      ...cachedUser,
      username: nextProfile.login,
      fullName: nextProfile.name,
      email: nextProfile.email,
      phone: nextProfile.phone,
      image: nextProfile.image,
    };

    setUser(nextUser);
    authService.saveUser(nextUser);
  };

  const openProfile = async () => {
    try {
      setProfileLoading(true);
      setProfileError("");
      setProfileSuccess("");
      const response = await authService.getProfile(mode);
      setProfile(response);
      syncCachedUser(response);
      setProfileForm({
        name: response.name || "",
        login: response.login || "",
        email: response.email || "",
        phone: response.phone || "",
        image: response.image || "",
      });
      setProfileOpen(true);
    } catch (error: any) {
      setProfileError(error.response?.data?.message || "Profile could not be loaded.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileFieldChange = (field: string, value: string) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileSave = async () => {
    try {
      setProfileSaving(true);
      setProfileError("");
      setProfileSuccess("");
      const response = await authService.updateProfile(
        {
          name: profileForm.name,
          login: profileForm.login,
          email: profileForm.email || undefined,
          phone: profileForm.phone || undefined,
        },
        mode
      );
      setProfile(response);
      setProfileForm((prev) => ({ ...prev, image: response.image || prev.image }));
      syncCachedUser(response);
      setProfileSuccess("Profile updated successfully.");
    } catch (error: any) {
      setProfileError(error.response?.data?.message || "Profile update failed.");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setProfileSaving(true);
      setProfileError("");
      setProfileSuccess("");
      const response = await authService.updateProfileImage(file, mode);
      setProfileForm((prev) => ({ ...prev, image: response.image }));
      if (profile) {
        const nextProfile = { ...profile, image: response.image };
        setProfile(nextProfile);
        syncCachedUser(nextProfile);
      }
      setProfileSuccess("Profile image updated successfully.");
    } catch (error: any) {
      setProfileError(error.response?.data?.message || "Image upload failed.");
    } finally {
      setProfileSaving(false);
      event.target.value = "";
    }
  };

  if (loading) {
    return <Loader message="Loading dashboard..." />;
  }

  if (!user) {
    return (
      <div className="error-state">
        <h2>Unable to load user data</h2>
        <button onClick={() => navigate(`/login?mode=${mode}`)} className="btn-primary">
          Return to Login
        </button>
      </div>
    );
  }

  const domains = user.domains || [];
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🏢 Enterprise Portal — {mode === "user" ? "Student" : "Admin"}</h1>
          </div>
          <div className="header-right">
            <div className="user-info">
              {user.image ? (
                <img
                  src={user.image}
                  alt={user.fullName || user.username}
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar user-avatar--placeholder">
                  {(user.fullName || user.username || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="user-meta">
                <span className="user-name">{user.fullName || user.username}</span>
              </div>
            </div>
            <button onClick={openProfile} className="profile-btn" disabled={profileLoading}>
              {profileLoading ? "Loading..." : "Edit Profile"}
            </button>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <section className="welcome-section">
          <h2>Welcome, {user.fullName || user.username}!</h2>
          <p>
            {domains.length > 0
              ? "Access your authorized domains below"
              : "You currently have no domain access. Please contact your administrator."}
          </p>
        </section>

        {domains.length > 0 && (
          <section className="domains-section">
            <h3>Available Domains</h3>
            <div className="domains-grid">
              {domains.map((domain) => (
                <a
                  key={domain.code}
                  href={domain.url}
                  className={`domain-card domain-${domain.code}`}
                >
                  <div className="domain-icon">{domain.icon}</div>
                  <div className="domain-content">
                    <h4>{domain.name}</h4>
                    <p>{domain.description}</p>
                    <span className="domain-link">Access Domain →</span>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

      </main>

      {profileOpen && (
        <div className="profile-modal-backdrop" onClick={() => setProfileOpen(false)}>
          <div className="profile-modal" onClick={(event) => event.stopPropagation()}>
            <div className="profile-modal__header">
              <h3>Edit Profile</h3>
              <button onClick={() => setProfileOpen(false)} className="profile-modal__close">
                Close
              </button>
            </div>

            {profileError ? <div className="profile-modal__error">{profileError}</div> : null}
            {profileSuccess ? <div className="profile-modal__success">{profileSuccess}</div> : null}

            <div className="profile-modal__body">
              <div className="profile-modal__image">
                {profileForm.image ? (
                  <img src={profileForm.image} alt="Profile" className="profile-modal__image-preview" />
                ) : (
                  <div className="profile-modal__image-placeholder">No image</div>
                )}
                <label className="profile-modal__upload">
                  <span>{profileSaving ? "Uploading..." : "Upload Image"}</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} hidden />
                </label>
              </div>

              <div className="profile-modal__fields">
                <label>
                  <span>Name</span>
                  <input
                    value={profileForm.name}
                    onChange={(event) => handleProfileFieldChange("name", event.target.value)}
                  />
                </label>
                <label>
                  <span>Login</span>
                  <input
                    value={profileForm.login}
                    onChange={(event) => handleProfileFieldChange("login", event.target.value)}
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input
                    value={profileForm.email}
                    onChange={(event) => handleProfileFieldChange("email", event.target.value)}
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    value={profileForm.phone}
                    onChange={(event) => handleProfileFieldChange("phone", event.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="profile-modal__footer">
              <button onClick={() => setProfileOpen(false)} className="secondary-btn">
                Cancel
              </button>
              <button onClick={handleProfileSave} className="primary-btn" disabled={profileSaving}>
                {profileSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
